import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { Form, Button, Table, Alert, Card, Modal, ProgressBar } from 'react-bootstrap';
import { useForm, usePage } from '@inertiajs/react';
import { router } from '@inertiajs/react';

import Valuation from '../../Data/Valuation';
import { PlStatementUpload as PlStatementUploadType, SOURCE_SYSTEMS } from '../../Data/PlStatementUpload';
import { Role } from '../../Data/Role';
import AuthedLayout from '../../Layouts/Authed';
import FormHeader from '../../Components/Forms/FormHeader';

interface Props {
    valuation: Valuation;
    uploads: PlStatementUploadType[];
}

const POLL_INTERVAL_MS = 5000;

const PlStatementUpload = (props: Props) => {
    const { flash, user, readOnly } = usePage().props as any;
    const isAdmin = user?.role === Role.admin;
    const canUpload = !readOnly;
    const [deleteUpload, setDeleteUpload] = useState<PlStatementUploadType | null>(null);
    const [deleting, setDeleting] = useState(false);
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const { data, setData, post, processing, progress, errors } = useForm({
        files: [] as File[],
        source_system: '',
    });

    // Auto-poll while any upload is pending or processing
    const hasInFlight = props.uploads.some(
        (u) => u.status === 'pending' || u.status === 'processing',
    );

    useEffect(() => {
        if (hasInFlight) {
            pollRef.current = setInterval(() => {
                router.reload({ only: ['uploads'] });
            }, POLL_INTERVAL_MS);
        } else {
            if (pollRef.current) clearInterval(pollRef.current);
        }

        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
        };
    }, [hasInFlight]);

    const handleDelete = () => {
        if (!deleteUpload) return;
        setDeleting(true);
        router.delete(
            `/valuation-form/${props.valuation.id}/pl-statement-upload/${deleteUpload.id}`,
            {
                onFinish: () => {
                    setDeleting(false);
                    setDeleteUpload(null);
                },
            },
        );
    };

    const submit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        post(`/valuation-form/${props.valuation.id}/pl-statement-upload`);
    };

    const getStatusBadge = (status: string) => {
        const badges: Record<string, { color: string; text: string }> = {
            pending:    { color: '#6c757d', text: 'Pending' },
            processing: { color: '#0dcaf0', text: 'Processing…' },
            parsed:     { color: '#0d6efd', text: 'Ready for Review' },
            approved:   { color: '#198754', text: 'Approved' },
            failed:     { color: '#dc3545', text: 'Failed' },
        };
        const badge = badges[status] || badges.pending;
        return (
            <span className="badge" style={{ backgroundColor: badge.color, color: '#fff' }}>
                {badge.text}
            </span>
        );
    };

    return (
        <div>
            <FormHeader
                agencyName={props.valuation.agency_name}
                effectiveDate={props.valuation.effective_date}
                pageName="Import P&L Statements"
            />

            {flash?.success && (
                <Alert variant="success" className="mb-4">
                    {flash.success}
                </Alert>
            )}

            {canUpload && <Card className="mb-4">
                <Card.Header>Upload P&L Statement PDF</Card.Header>
                <Card.Body>
                    <p className="text-muted mb-3">
                        Upload one or more PDF Profit &amp; Loss statements.
                        {isAdmin && ' The system will use AI to extract line items and suggest category mappings for your review.'}
                    </p>

                    <Form onSubmit={submit}>
                        <Form.Group className="mb-3">
                            <Form.Label>Select PDF File(s)</Form.Label>
                            <Form.Control
                                type="file"
                                accept=".pdf"
                                multiple
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    setData('files', Array.from(e.target.files ?? []))
                                }
                                isInvalid={!!errors.files}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.files}
                            </Form.Control.Feedback>
                            <Form.Text className="text-muted">
                                Maximum 10 files, 10 MB each. Both digital and scanned PDFs are
                                supported.
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Source System <span className="text-muted fw-normal">(optional)</span></Form.Label>
                            <Form.Select
                                value={data.source_system}
                                onChange={(e) => setData('source_system', e.target.value)}
                            >
                                <option value=""></option>
                                {SOURCE_SYSTEMS.map((s) => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </Form.Select>
                            <Form.Text className="text-muted">
                                The accounting or agency management system this statement was exported from.
                            </Form.Text>
                        </Form.Group>

                        {progress && (
                            <ProgressBar
                                now={progress.percentage}
                                label={`${progress.percentage}%`}
                                className="mb-3"
                            />
                        )}

                        <Button type="submit" disabled={data.files.length === 0 || processing}>
                            {processing
                                ? 'Uploading…'
                                : data.files.length > 1
                                ? `Upload ${data.files.length} Files`
                                : isAdmin ? 'Upload & Analyze' : 'Upload'}
                        </Button>
                    </Form>
                </Card.Body>
            </Card>}

            {props.uploads.length > 0 && (
                <Card>
                    <Card.Header className="d-flex justify-content-between align-items-center">
                        <span>Previous Uploads</span>
                        {isAdmin && hasInFlight && (
                            <span className="text-muted small">
                                <span
                                    className="spinner-border spinner-border-sm me-1"
                                    role="status"
                                />
                                Processing…
                            </span>
                        )}
                    </Card.Header>
                    <Card.Body>
                        <Table striped bordered hover>
                            <thead>
                                <tr>
                                    <th>Filename</th>
                                    <th>Source System</th>
                                    {isAdmin && <th>Status</th>}
                                    <th>Uploaded</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {props.uploads.map((upload) => (
                                    <tr key={upload.id}>
                                        <td>{upload.original_filename}</td>
                                        <td>
                                            <small className="text-muted">
                                                {upload.source_system || '—'}
                                            </small>
                                        </td>
                                        {isAdmin && <td>{getStatusBadge(upload.status)}</td>}
                                        <td>
                                            {new Date(upload.created_at).toLocaleDateString()}
                                        </td>
                                        <td>
                                            <a
                                                href={`/valuation-form/${props.valuation.id}/pl-statement-upload/${upload.id}/file`}
                                                className="btn btn-sm btn-outline-primary me-1"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                View
                                            </a>
                                            {isAdmin && upload.status === 'parsed' && (
                                                <a
                                                    href={`/valuation-form/${props.valuation.id}/pl-statement-review/${upload.id}`}
                                                    className="btn btn-sm btn-primary me-1"
                                                >
                                                    Review
                                                </a>
                                            )}
                                            {isAdmin && upload.status === 'approved' && !readOnly && (
                                                <a
                                                    href={`/valuation-form/${props.valuation.id}/pl-statement-review/${upload.id}`}
                                                    className="btn btn-sm btn-outline-primary me-1"
                                                >
                                                    Edit
                                                </a>
                                            )}
                                            {isAdmin && upload.status === 'failed' &&
                                                upload.error_message && (
                                                    <span className="text-danger small me-2">
                                                        {upload.error_message}
                                                    </span>
                                                )}
                                            {(isAdmin || upload.status !== 'approved') && !readOnly && (
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() => setDeleteUpload(upload)}
                                                >
                                                    Remove
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            )}

            <Modal show={!!deleteUpload} onHide={() => setDeleteUpload(null)}>
                <Modal.Header closeButton>
                    <Modal.Title>Remove Upload</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to remove{' '}
                    <strong>{deleteUpload?.original_filename}</strong>?
                    {deleteUpload?.status === 'approved' && (
                        <Alert variant="warning" className="mt-3 mb-0">
                            This upload has been approved. Removing it will also remove the
                            imported P&amp;L data.
                        </Alert>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setDeleteUpload(null)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleDelete} disabled={deleting}>
                        {deleting ? 'Removing…' : 'Yes, Remove'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

PlStatementUpload.layout = (page: ReactNode) => <AuthedLayout>{page}</AuthedLayout>;

export default PlStatementUpload;
